import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminLoans, useUpdateLoanStatus } from "../../hooks/useAdmin";
import Card from "../../components/ui/Card";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import StatCard from "../../components/ui/StatCard";
import RiskMeter from "../../components/ui/RiskMeter";


const fmt = (n:number) => new Intl.NumberFormat("en-NG",{style:"currency",currency:"NGN", currencyDisplay:"symbol",maximumFractionDigits:0}).format(n);
const fmtDate = (d:string) => d ? new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—";
const statusColor = (s:string):any => s==="active"?"emerald":s==="repaid"?"blue":s==="overdue"?"red":s==="pending-surety"?"amber":"slate";

export default function AdminLoansPage() {  
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const { data: loans = [] } = useAdminLoans(filter !== "all" ? {status:filter} : undefined) as any;
  const updateStatus = useUpdateLoanStatus();

  const totalDisbursed   = loans.reduce((s:number,l:any)=>s+Number(l.amount),0);
  const totalRepaid      = loans.reduce((s:number,l:any)=>s+Number(l.amount_repaid),0);
  const totalOutstanding = loans.filter((l:any)=>["active","overdue"].includes(l.status)).reduce((s:number,l:any)=>s+(Number(l.amount)-Number(l.amount_repaid)),0);
  const avgRisk          = loans.length ? Math.round(loans.reduce((s:number,l:any)=>s+(l.risk_score||60),0)/loans.length) : 0;

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total Disbursed"  value={fmt(totalDisbursed)}   color="text-blue-600"/>
        <StatCard label="Outstanding"      value={fmt(totalOutstanding)}  color="text-violet-600"/>
        <StatCard label="Recovery Rate"    value={totalDisbursed?`${Math.round((totalRepaid/totalDisbursed)*100)}%`:"0%"} color="text-emerald-600"/>
        <StatCard label="Avg Risk Score"   value={avgRisk}                sub="higher = safer" color="text-slate-700"/>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {["all","approved","disbursed","repaid","overdue","pending-surety"].map(f => (
          <Button key={f} size="sm" variant={filter===f?"primary":"secondary"} onClick={()=>setFilter(f)}>
            {f==="all"?"All":f==="disbursed"?"Active":f==="pending-surety"?"Pending":f.charAt(0).toUpperCase()+f.slice(1)}
          </Button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-50">
              {["ID","Borrower","Amount","Repaid","Status","Risk","Due","Actions"].map(h=>(
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-slate-500 border-b border-slate-200">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loans.map((l:any) => (
              <tr key={l.id} className="border-b border-slate-100">
                <td className="px-4 py-3 text-xs font-medium text-slate-500">{l.id?.slice(0,8)}…</td>
                <td className="px-4 py-3 text-sm text-slate-800">{l.borrower}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-800">{fmt(l.amount)}</td>
                <td className="px-4 py-3">
                  <p className="text-sm text-emerald-600">{fmt(l.amount_repaid||0)}</p>
                  <div className="w-20 h-1 bg-slate-100 rounded-full mt-1 overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{width:`${Math.round(((l.amount_repaid||0)/l.amount)*100)}%`}}/>
                  </div>
                </td>
                <td className="px-4 py-3"><Badge label={l.status} color={statusColor(l.status)}/></td>
                <td className="px-4 py-3 min-w-[100px]"><RiskMeter score={l.risk_score||60}/></td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(l.due_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    {l.status==="overdue"        && <Button size="sm" variant="danger"    onClick={()=>updateStatus.mutate({id:l.id,status:"defaulted"})}>Flag Default</Button>}
                    {l.status==="pending-surety" && <Button size="sm" variant="amber"     onClick={()=>updateStatus.mutate({id:l.id,status:"active"})}>Force Approve</Button>}
                    {["active","overdue"].includes(l.status) && <Button size="sm" variant="secondary">📧 Remind</Button>}
                    {l.status==="approved" && <Button size="sm" variant="primary" onClick={() => navigate(`/admin/loans/${l.id}`)}>Disburse Loan</Button>}
                  </div>
                </td>
              </tr>
            ))}
            {loans.length===0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400">No loans found</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
